
import { createContext, useContext, useState, useRef, useEffect } from "react"
import { Link } from "react-router-dom"
import { useTheme } from "next-themes"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { SEOHead } from "@/components/seo-head"

const brandRed = "#C0392B"
const brandGreen = "#4A8C3F"
const brandBlue = "#2E5BA8"

const brandRedLight = "#E74C3C"
const brandGreenLight = "#6ECB63"
const brandBlueLight = "#5B9BD5"

// ─── i18n ────────────────────────────────────────────────────────────────────

type Lang = "en" | "sq" | "it"
type ModuleKey = "business" | "classes" | "workout" | "diet" | "store" | "whitelabel" | "marketing" | "payments"

const LANGS: { code: Lang; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "sq", label: "Shqip",   flag: "🇦🇱" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
]

interface T {
  nav: { signIn: string; getStarted: string }
  hero: {
    badge: string; heading1: string; heading2: string
    description: string; cta1: string; cta2: string
    footnote: string; membersLabel: string
  }
  stats: { gyms: string; activeMembers: string; uptimeSla: string; support: string }
  features: {
    heading: string; subheading: string
    cards: { title: string; desc_short: string; desc: string }[]
  }
  pricing: {
    heading: string; subheading: string
    comingSoon: string; soon: string; perMonth: string; perMonthEach: string
    modules: Record<ModuleKey, { name: string; features: string[] }>
    extraLocations: { name: string; features: string[] }
    calculator: {
      title: string; selectAll: string; extraLocations: string
      modules: (n: number) => string; extraLocs: (n: number) => string; total: string
    }
    cta: { sub: string; btn1: string; btn2: string }
  }
  benefits: { heading: string; subheading: string; items: { title: string; desc: string }[] }
  caseStudy: {
    label: string; heading1: string; heading2: string; description: string
    gyms: { subtitle: string; members: string; highlight: string; features: string[] }[]
    followInstagram: string; testimonial: string; operator: string; statsLabels: string[]
  }
  cta: {
    heading1: string; heading2: string; description: string
    btn1: string; btn2: string; check1: string; check2: string; check3: string
  }
  footer: {
    tagline: string
    product: string; productLinks: string[]
    company: string; companyLinks: string[]
    legal: string; legalLinks: string[]
    copyright: string
  }
}

const translations: Record<Lang, T> = {
  // ── English ────────────────────────────────────────────────────────────────
  en: {
    nav: { signIn: "Sign In", getStarted: "Get Started" },
    hero: {
      badge: "Modern Fitness Management",
      heading1: "Transform Your",
      heading2: "Fitness Business",
      description: "The all-in-one platform for gym and fitness center management. Streamline operations, engage members, and grow your business.",
      cta1: "Start Free Trial",
      cta2: "Schedule Demo",
      footnote: "No credit card required · 14-day free trial · Cancel anytime",
      membersLabel: "Members",
    },
    stats: { gyms: "Gyms", activeMembers: "Active Members", uptimeSla: "Uptime SLA", support: "Support" },
    features: {
      heading: "Everything You Need to Scale",
      subheading: "A comprehensive platform built for fitness businesses that want to grow",
      cards: [
        { title: "White-Label Multi-Tenancy",    desc_short: "Built for franchises and chains",      desc: "Manage unlimited locations from one platform. Each gym gets custom branding, domains, and full operational independence." },
        { title: "Member Management",             desc_short: "360° member view",                    desc: "Complete member profiles, smart segmentation, attendance tracking, and engagement analytics to maximize retention." },
        { title: "Smart Class Scheduling",        desc_short: "Automated booking management",        desc: "Intelligent class scheduling with capacity management, waitlists, automated reminders, and trainer assignments." },
        { title: "Payment Processing",            desc_short: "Automated revenue management",        desc: "Recurring billing, payment plans, invoicing, and comprehensive financial reporting. Accept all major payment methods." },
        { title: "Advanced Analytics",            desc_short: "Business intelligence dashboard",     desc: "Real-time insights on revenue, attendance, member behavior, and operational KPIs to drive growth decisions." },
        { title: "Nutrition & Training Plans",    desc_short: "Personalized member programs",        desc: "Create and assign custom diet and exercise plans. Track member progress and adjust programs with AI-powered recommendations." },
      ],
    },
    pricing: {
      heading: "Flexible Modular Pricing",
      subheading: "Pay only for the modules you need. Add or remove features as your business grows.",
      comingSoon: "COMING SOON", soon: "SOON", perMonth: "/month", perMonthEach: "/month each",
      modules: {
        business:   { name: "Business Management", features: ["Member Check-Ins (QR, Card, PIN)", "Multi-Location Dashboard", "Employee Management", "Reports & Analytics"] },
        classes:    { name: "Classes",             features: ["Scheduling (recurring & one-off)", "Self-service bookings & waitlists", "Capacity & trainer assignment", "Automated notifications"] },
        workout:    { name: "Workout & Training",  features: ["Workout Plan Creator", "Workout Logger", "Exercise Library", "Performance Reporting"] },
        diet:       { name: "Diet Planner",        features: ["Personalized meal plans", "Food database (local foods)", "Calorie & macro tracking", "Workout plan integration"] },
        store:      { name: "Store",               features: ["Product catalog & categories", "Order management & tracking", "Configurable order workflows", "Food, supplements, merch & more"] },
        whitelabel: { name: "White Label",         features: ["Personal iOS & Android apps", "Personalized website", "Custom domain", "Full brand customization"] },
        marketing:  { name: "Marketing & CRM",     features: ["CRM with member profiles", "Automated renewal reminders", "Email, SMS, Push & WhatsApp", "Onboarding automation"] },
        payments:   { name: "Payments",            features: ["Stripe & PayPal Integration", "Pokpay (local fintech)", "Recurring billing", "Payment reports & tracking"] },
      },
      extraLocations: { name: "Extra Locations", features: ["Full module access per location", "Separate management & reports", "Custom branding per gym", "Independent operations"] },
      calculator: {
        title: "Pricing Calculator", selectAll: "Select All", extraLocations: "Extra Locations",
        modules: (n) => `${n} module${n !== 1 ? "s" : ""}`,
        extraLocs: (n) => `+${n} extra location${n !== 1 ? "s" : ""}`,
        total: "Total",
      },
      cta: { sub: "Start with the essentials and add modules as you grow", btn1: "Get Started Now", btn2: "Talk to Sales" },
    },
    benefits: {
      heading: "Why THE NOT FIT?",
      subheading: "The most advanced fitness management platform, trusted by industry leaders",
      items: [
        { title: "AI-Powered Diet Management",       desc: "Generate personalised nutrition plans in seconds. AI adapts macros and meal schedules based on each member's goals, allergies, and body metrics." },
        { title: "AI Workout Plan Builder",           desc: "Smart training programs built around fitness level, recovery data, and performance trends. Plans evolve automatically as members progress." },
        { title: "Multi-Provider Access Control",     desc: "Grant gym entry via QR code, NFC card, PIN, or biometric scan — across all locations. Manage and revoke access from one dashboard instantly." },
        { title: "Member Progress Tracking",          desc: "Body composition scans, strength records, and cardio benchmarks stored per member. Trainers get instant visibility into each client's trajectory." },
        { title: "Automated Retention Engine",        desc: "Churn prediction flags at-risk members before they cancel. Trigger personalised re-engagement offers, check-in streaks, and milestone rewards automatically." },
        { title: "Real-Time Operations Dashboard",    desc: "Live floor occupancy, class fill rates, trainer utilisation, and revenue streams — all in one view across every location you operate." },
      ],
    },
    caseStudy: {
      label: "Case Study", heading1: "Powering the", heading2: "Flex Gym Group",
      description: "10,000+ members across multiple locations — managed seamlessly on THE NOT FIT platform. From premium gyms to CrossFit studios to in-house restaurants, one system does it all.",
      gyms: [
        { subtitle: "Premium Gym in the Capital", members: "2,000+", highlight: "Members", features: ["Full equipment floor", "Group classes", "Personal training", "Sauna & recovery"] },
        { subtitle: "Premium Seaside Gym",         members: "1,500+", highlight: "Members", features: ["Seaside training", "Outdoor areas", "Seasonal programs", "Resort integration"] },
        { subtitle: "CrossFit, Pilates & Restaurant", members: "1,200+", highlight: "Members", features: ["CrossFit box", "Pilates studio", "In-house restaurant", "Sports nutrition bar"] },
      ],
      followInstagram: "Follow on Instagram",
      testimonial: '"THE NOT FIT lets us manage 3 completely different gym concepts — from a premium fitness center to a CrossFit box with a restaurant — all under one platform. Our members love the seamless experience."',
      operator: "Multi-location fitness operator, Albania",
      statsLabels: ["Locations", "Total Members", "Classes / Week", "Platform"],
    },
    cta: {
      heading1: "Ready to Scale Your", heading2: "Fitness Empire?",
      description: "Join the growing number of fitness businesses that trust THE NOT FIT to power their operations.\nStart your free 14-day trial today—no credit card required.",
      btn1: "Start Free Trial", btn2: "Talk to Sales",
      check1: "14-day free trial", check2: "No credit card", check3: "Cancel anytime",
    },
    footer: {
      tagline: "Modern fitness management platform for gyms, studios, and fitness businesses.",
      product: "Product",   productLinks: ["Features", "Pricing", "Security", "Integrations"],
      company: "Company",   companyLinks: ["About", "Contact", "Careers", "Blog"],
      legal: "Legal",       legalLinks: ["Privacy Policy", "Terms of Service", "Data Deletion", "Support"],
      copyright: "© 2026 THE NOT FIT. All rights reserved.",
    },
  },

  // ── Albanian / Shqip ───────────────────────────────────────────────────────
  sq: {
    nav: { signIn: "Hyr", getStarted: "Fillo Tani" },
    hero: {
      badge: "Menaxhim Modern i Fitness",
      heading1: "Transformo",
      heading2: "Biznesin Tënd të Fitness",
      description: "Platforma gjithëpërfshirëse për menaxhimin e palestrës dhe qendrës fitness. Optimizo operacionet, angazho anëtarët dhe rrit biznesin tënd.",
      cta1: "Fillo Provën Falas",
      cta2: "Rezervo Demo",
      footnote: "Pa kartë krediti · Provë falas 14-ditore · Anulo kurrë kur",
      membersLabel: "Anëtarë",
    },
    stats: { gyms: "Palestra", activeMembers: "Anëtarë Aktivë", uptimeSla: "Uptime SLA", support: "Mbështetje" },
    features: {
      heading: "Gjithçka që Keni Nevojë",
      subheading: "Platformë gjithëpërfshirëse e ndërtuar për bizneset fitness që duan të rriten",
      cards: [
        { title: "Multi-Tenant White-Label",         desc_short: "Ndërtuar për franshiza dhe zinxhirë",    desc: "Menaxho lokacione të pakufizuara nga një platformë. Çdo palestrë merr markë, domene dhe pavarësi operative të personalizuar." },
        { title: "Menaxhimi i Anëtarëve",             desc_short: "Pamje 360° e anëtarit",                  desc: "Profile të plota të anëtarëve, segmentim i mençur, gjurmim i pranisë dhe analitikë angazhimi për të maksimizuar mbajtjen." },
        { title: "Planifikim i Mençur i Klasave",     desc_short: "Menaxhim automatik i rezervimeve",       desc: "Planifikim inteligjent i klasave me menaxhim kapaciteti, lista pritjeje, rikujtues automatikë dhe caktim trajnerësh." },
        { title: "Përpunimi i Pagesave",              desc_short: "Menaxhim automatik i të ardhurave",      desc: "Faturim i përsëritur, plane pagesash, faturim dhe raportim financiar gjithëpërfshirës. Pranoni të gjitha metodat kryesore të pagesës." },
        { title: "Analitikë e Avancuar",              desc_short: "Panel i inteligjencës së biznesit",      desc: "Të dhëna në kohë reale mbi të ardhurat, praninë, sjelljen e anëtarëve dhe KPI-të operative për vendime rritjeje." },
        { title: "Plane Ushqyerjeje & Trajnimi",      desc_short: "Programe të personalizuara",             desc: "Krijoni dhe caktoni plane të personalizuara ushqyerjeje dhe ushtrimesh. Gjurmoni progresin me rekomandime të bazuara në AI." },
      ],
    },
    pricing: {
      heading: "Çmime Modulare Fleksibël",
      subheading: "Paguani vetëm për modulet që keni nevojë. Shtoni ose hiqni veçori ndërsa biznesi juaj rritet.",
      comingSoon: "SHPEJT", soon: "SHPEJT", perMonth: "/muaj", perMonthEach: "/muaj secila",
      modules: {
        business:   { name: "Menaxhimi i Biznesit",  features: ["Hyrje Anëtarësh (QR, Kartë, PIN)", "Panel Multi-Lokacion", "Menaxhimi i Punonjësve", "Raporte & Analitikë"] },
        classes:    { name: "Klasa",                  features: ["Planifikim (i përsëritur & njëherësh)", "Rezervime & lista pritjeje", "Kapacitet & caktim trajnerësh", "Njoftime automatike"] },
        workout:    { name: "Stërvitje & Trajnim",    features: ["Krijues i Planit të Stërvitjes", "Regjistër Stërvitjeje", "Bibliotekë Ushtrimesh", "Raportim Performancës"] },
        diet:       { name: "Planifikues Diete",      features: ["Plane vakte të personalizuara", "Bazë ushqimore (ushqime lokale)", "Gjurmim kalori & makro", "Integrim me planin e stërvitjes"] },
        store:      { name: "Dyqan",                  features: ["Katalog produktesh & kategori", "Menaxhim & gjurmim porosish", "Rrjedha e konfigurueshme", "Ushqime, suplemente, merch & më shumë"] },
        whitelabel: { name: "White Label",            features: ["Aplikacione personale iOS & Android", "Faqe interneti e personalizuar", "Domen i personalizuar", "Personalizim i plotë i markës"] },
        marketing:  { name: "Marketing & CRM",        features: ["CRM me profile anëtarësh", "Rikujtues automatikë rinovimi", "Email, SMS, Push & WhatsApp", "Automatizim onboarding"] },
        payments:   { name: "Pagesa",                 features: ["Integrim Stripe & PayPal", "Pokpay (fintech lokal)", "Faturim i përsëritur", "Raporte & gjurmim pagesash"] },
      },
      extraLocations: { name: "Lokacione Shtesë", features: ["Akses i plotë modulesh për lokacion", "Menaxhim & raporte të veçanta", "Markë e personalizuar për çdo palestrë", "Operacione të pavarura"] },
      calculator: {
        title: "Kalkulator Çmimesh", selectAll: "Zgjidh Të Gjitha", extraLocations: "Lokacione Shtesë",
        modules: (n) => `${n} modul${n !== 1 ? "e" : ""}`,
        extraLocs: (n) => `+${n} lokacion${n !== 1 ? "e" : ""} shtesë`,
        total: "Totali",
      },
      cta: { sub: "Filloni me bazën dhe shtoni module ndërsa rriteni", btn1: "Fillo Tani", btn2: "Fol me Shitjet" },
    },
    benefits: {
      heading: "Pse THE NOT FIT?",
      subheading: "Platforma më e avancuar e menaxhimit të fitness, e besuar nga liderët e industrisë",
      items: [
        { title: "Menaxhim Diete me AI",              desc: "Gjeneroni plane ushqyerjeje të personalizuara në sekonda. AI adapton makrot dhe oraret e vakteve bazuar në qëllimet, alergjiite dhe metrikat trupore." },
        { title: "Krijues Planesh Stërvitjeje me AI", desc: "Programe trajnimi të ndërtuara rreth nivelit të fitnesit, të dhënave të rikuperimit dhe trendeve të performancës. Planet evoluojnë automatikisht." },
        { title: "Kontroll Aksesi Multi-Provider",    desc: "Jepni hyrje në palestrë nëpërmjet QR kodit, kartës NFC, PIN ose skanimit biometrik — në të gjitha lokacionet. Menaxhoni aksesin nga një panel." },
        { title: "Gjurmim i Progresit të Anëtarëve", desc: "Skanim i përbërjes trupore, rekorde forcë dhe standarde kardio të ruajtura për çdo anëtar. Trajnerët marrin dukshmëri të menjëhershme." },
        { title: "Motor Automatik Mbajtjeje",         desc: "Parashikimi i largimit sinjalizon anëtarët në rrezik para se të anulojnë. Aktivizoni automatikisht oferta riangažhimi dhe shpërblime." },
        { title: "Panel Operacionesh në Kohë Reale",  desc: "Zënia e katit live, normat e mbushjes së klasave, shfrytëzimi i trajnerëve dhe rrjedhat e të ardhurave — të gjitha në çdo lokacion." },
      ],
    },
    caseStudy: {
      label: "Rast Studimi", heading1: "Duke Fuqizuar", heading2: "Flex Gym Group",
      description: "Mbi 10,000 anëtarë nëpër lokacione të shumëfishta — të menaxhuar pa probleme në platformën THE NOT FIT. Nga palestra premium deri te studioja CrossFit dhe restorantet e brendshme, një sistem bën gjithçka.",
      gyms: [
        { subtitle: "Palestrë Premium në Kryeqytet",  members: "2,000+", highlight: "Anëtarë", features: ["Kat i plotë pajisjesh", "Klasa grupore", "Trajnim personal", "Sauna & rikuperim"] },
        { subtitle: "Palestrë Premium në Bregdet",    members: "1,500+", highlight: "Anëtarë", features: ["Trajnim në bregdet", "Zona jashtë", "Programe sezonale", "Integrim resort"] },
        { subtitle: "CrossFit, Pilates & Restorant",  members: "1,200+", highlight: "Anëtarë", features: ["Box CrossFit", "Studio Pilates", "Restorant i brendshëm", "Bar ushqyerjeje sportive"] },
      ],
      followInstagram: "Ndiqni në Instagram",
      testimonial: '"THE NOT FIT na lejon të menaxhojmë 3 koncepte krejt të ndryshme palestrash — nga qendra premium fitness deri te box CrossFit me restorant — të gjitha nën një platformë. Anëtarët tanë e duan përvojën e qetë."',
      operator: "Operues fitness me shumë lokacione, Shqipëri",
      statsLabels: ["Lokacione", "Anëtarë Gjithsej", "Klasa / Javë", "Platformë"],
    },
    cta: {
      heading1: "Gati të Shkallëzoni", heading2: "Perandorinë Tuaj Fitness?",
      description: "Bashkohuni me numrin në rritje të bizneseve fitness që besojnë te THE NOT FIT.\nFilloni provën falas 14-ditore sot — pa kartë krediti.",
      btn1: "Fillo Provën Falas", btn2: "Fol me Shitjet",
      check1: "Provë falas 14-ditore", check2: "Pa kartë krediti", check3: "Anulo kurrë kur",
    },
    footer: {
      tagline: "Platformë moderne e menaxhimit të fitness për palestra, studio dhe biznese fitness.",
      product: "Produkti",  productLinks: ["Veçoritë", "Çmimet", "Siguria", "Integrimet"],
      company: "Kompania",  companyLinks: ["Rreth nesh", "Kontakt", "Karriera", "Blog"],
      legal: "Ligjore",     legalLinks: ["Politika e Privatësisë", "Kushtet e Shërbimit", "Fshirja e të Dhënave", "Mbështetje"],
      copyright: "© 2026 THE NOT FIT. Të gjitha të drejtat e rezervuara.",
    },
  },

  // ── Italian / Italiano ─────────────────────────────────────────────────────
  it: {
    nav: { signIn: "Accedi", getStarted: "Inizia Ora" },
    hero: {
      badge: "Gestione Moderna del Fitness",
      heading1: "Trasforma il Tuo",
      heading2: "Business Fitness",
      description: "La piattaforma all-in-one per la gestione di palestre e centri fitness. Ottimizza le operazioni, coinvolgi i soci e fai crescere il tuo business.",
      cta1: "Inizia la Prova Gratuita",
      cta2: "Prenota una Demo",
      footnote: "Nessuna carta di credito · Prova gratuita 14 giorni · Cancella quando vuoi",
      membersLabel: "Soci",
    },
    stats: { gyms: "Palestre", activeMembers: "Soci Attivi", uptimeSla: "Uptime SLA", support: "Supporto" },
    features: {
      heading: "Tutto Ciò che Ti Serve per Crescere",
      subheading: "Una piattaforma completa costruita per le imprese fitness che vogliono espandersi",
      cards: [
        { title: "Multi-Tenancy White-Label",           desc_short: "Costruito per franchising e catene",       desc: "Gestisci sedi illimitate da un'unica piattaforma. Ogni palestra ottiene branding personalizzato, domini e piena indipendenza operativa." },
        { title: "Gestione Soci",                        desc_short: "Visione a 360° del socio",                 desc: "Profili completi dei soci, segmentazione intelligente, monitoraggio presenze e analisi del coinvolgimento per massimizzare la fidelizzazione." },
        { title: "Pianificazione Corsi Intelligente",    desc_short: "Gestione automatica delle prenotazioni",   desc: "Pianificazione intelligente dei corsi con gestione della capacità, liste d'attesa, promemoria automatici e assegnazione istruttori." },
        { title: "Elaborazione Pagamenti",               desc_short: "Gestione automatica dei ricavi",           desc: "Fatturazione ricorrente, piani di pagamento, fatturazione e reportistica finanziaria completa. Accetta tutti i principali metodi di pagamento." },
        { title: "Analisi Avanzate",                     desc_short: "Dashboard di business intelligence",       desc: "Informazioni in tempo reale su ricavi, presenze, comportamento dei soci e KPI operativi per guidare le decisioni di crescita." },
        { title: "Piani Nutrizionali & di Allenamento",  desc_short: "Programmi personalizzati per i soci",      desc: "Crea e assegna piani dieta ed esercizi personalizzati. Monitora i progressi con raccomandazioni basate sull'intelligenza artificiale." },
      ],
    },
    pricing: {
      heading: "Prezzi Modulari Flessibili",
      subheading: "Paga solo per i moduli di cui hai bisogno. Aggiungi o rimuovi funzionalità man mano che il tuo business cresce.",
      comingSoon: "PROSSIMAMENTE", soon: "PRESTO", perMonth: "/mese", perMonthEach: "/mese cad.",
      modules: {
        business:   { name: "Gestione Aziendale",         features: ["Check-in Soci (QR, Tessera, PIN)", "Dashboard Multi-Sede", "Gestione Dipendenti", "Report & Analisi"] },
        classes:    { name: "Corsi",                       features: ["Pianificazione (ricorrente & una tantum)", "Prenotazioni & liste d'attesa", "Capienza & assegnazione istruttori", "Notifiche automatiche"] },
        workout:    { name: "Allenamento & Training",      features: ["Creatore Piano di Allenamento", "Diario Allenamento", "Libreria Esercizi", "Report Prestazioni"] },
        diet:       { name: "Pianificatore Dieta",         features: ["Piani pasto personalizzati", "Database alimenti (cibi locali)", "Monitoraggio calorie & macro", "Integrazione piano allenamento"] },
        store:      { name: "Negozio",                     features: ["Catalogo prodotti & categorie", "Gestione & monitoraggio ordini", "Flussi ordini configurabili", "Cibo, integratori, merch e altro"] },
        whitelabel: { name: "White Label",                 features: ["App iOS & Android personalizzate", "Sito web personalizzato", "Dominio personalizzato", "Personalizzazione completa del brand"] },
        marketing:  { name: "Marketing & CRM",             features: ["CRM con profili soci", "Promemoria rinnovo automatici", "Email, SMS, Push & WhatsApp", "Automazione onboarding"] },
        payments:   { name: "Pagamenti",                   features: ["Integrazione Stripe & PayPal", "Pokpay (fintech locale)", "Fatturazione ricorrente", "Report & monitoraggio pagamenti"] },
      },
      extraLocations: { name: "Sedi Aggiuntive", features: ["Accesso completo ai moduli per sede", "Gestione & report separati", "Branding personalizzato per palestra", "Operazioni indipendenti"] },
      calculator: {
        title: "Calcolatore Prezzi", selectAll: "Seleziona Tutti", extraLocations: "Sedi Aggiuntive",
        modules: (n) => `${n} modul${n !== 1 ? "i" : "o"}`,
        extraLocs: (n) => `+${n} sed${n !== 1 ? "i" : "e"} aggiuntiv${n !== 1 ? "e" : "a"}`,
        total: "Totale",
      },
      cta: { sub: "Inizia con l'essenziale e aggiungi moduli man mano che cresci", btn1: "Inizia Ora", btn2: "Parla con le Vendite" },
    },
    benefits: {
      heading: "Perché THE NOT FIT?",
      subheading: "La piattaforma di gestione fitness più avanzata, fidata dai leader del settore",
      items: [
        { title: "Gestione Dieta con AI",                   desc: "Genera piani nutrizionali personalizzati in pochi secondi. L'AI adatta i macro e gli orari dei pasti in base agli obiettivi, alle allergie e alle metriche corporee." },
        { title: "Costruttore Piani di Allenamento con AI", desc: "Programmi di allenamento costruiti attorno al livello di fitness, ai dati di recupero e alle tendenze di prestazione. I piani si evolvono automaticamente." },
        { title: "Controllo Accessi Multi-Provider",        desc: "Concedi l'accesso alla palestra tramite QR code, tessera NFC, PIN o scansione biometrica — in tutte le sedi. Gestisci e revoca gli accessi istantaneamente." },
        { title: "Monitoraggio Progressi Soci",             desc: "Scansioni della composizione corporea, record di forza e benchmark cardio memorizzati per socio. Gli istruttori hanno visibilità immediata su ogni cliente." },
        { title: "Motore di Fidelizzazione Automatico",     desc: "La previsione del churn segnala i soci a rischio prima che cancellino. Attiva automaticamente offerte personalizzate, streak di check-in e premi milestone." },
        { title: "Dashboard Operativo in Tempo Reale",      desc: "Occupazione live della sala, tassi di riempimento corsi, utilizzo istruttori e flussi di ricavi — tutto in un'unica vista per ogni sede." },
      ],
    },
    caseStudy: {
      label: "Caso di Studio", heading1: "Al Servizio del", heading2: "Flex Gym Group",
      description: "Oltre 10.000 soci in più sedi — gestiti senza problemi sulla piattaforma THE NOT FIT. Dalle palestre premium alle box CrossFit ai ristoranti interni, un solo sistema fa tutto.",
      gyms: [
        { subtitle: "Palestra Premium nella Capitale",  members: "2.000+", highlight: "Soci", features: ["Piano attrezzature completo", "Corsi di gruppo", "Personal training", "Sauna & recupero"] },
        { subtitle: "Palestra Premium sul Mare",         members: "1.500+", highlight: "Soci", features: ["Allenamento in riva al mare", "Aree esterne", "Programmi stagionali", "Integrazione resort"] },
        { subtitle: "CrossFit, Pilates & Ristorante",   members: "1.200+", highlight: "Soci", features: ["Box CrossFit", "Studio Pilates", "Ristorante interno", "Bar nutrizione sportiva"] },
      ],
      followInstagram: "Seguici su Instagram",
      testimonial: '"THE NOT FIT ci permette di gestire 3 concetti di palestra completamente diversi — da un centro fitness premium a una box CrossFit con ristorante — tutto sotto un\'unica piattaforma. I nostri soci adorano l\'esperienza."',
      operator: "Operatore fitness multi-sede, Albania",
      statsLabels: ["Sedi", "Soci Totali", "Corsi / Settimana", "Piattaforma"],
    },
    cta: {
      heading1: "Pronto a Scalare il Tuo", heading2: "Imperio Fitness?",
      description: "Unisciti al numero crescente di imprese fitness che si affidano a THE NOT FIT.\nInizia la prova gratuita di 14 giorni oggi — nessuna carta di credito richiesta.",
      btn1: "Inizia la Prova Gratuita", btn2: "Parla con le Vendite",
      check1: "Prova gratuita 14 giorni", check2: "Nessuna carta di credito", check3: "Cancella quando vuoi",
    },
    footer: {
      tagline: "Piattaforma moderna di gestione fitness per palestre, studi e imprese fitness.",
      product: "Prodotto",  productLinks: ["Funzionalità", "Prezzi", "Sicurezza", "Integrazioni"],
      company: "Azienda",   companyLinks: ["Chi Siamo", "Contatti", "Carriere", "Blog"],
      legal: "Legale",      legalLinks: ["Informativa sulla Privacy", "Termini di Servizio", "Cancellazione Dati", "Supporto"],
      copyright: "© 2026 THE NOT FIT. Tutti i diritti riservati.",
    },
  },
}

// ─── Static data (non-translatable) ──────────────────────────────────────────

const MODULES_CONFIG = [
  { id: "business"   as ModuleKey, price: 149 },
  { id: "classes"    as ModuleKey, price: 149 },
  { id: "workout"    as ModuleKey, price: 99  },
  { id: "diet"       as ModuleKey, price: 99  },
  { id: "store"      as ModuleKey, price: 99  },
  { id: "whitelabel" as ModuleKey, price: 99  },
  { id: "marketing"  as ModuleKey, price: 49, comingSoon: true },
  { id: "payments"   as ModuleKey, price: 49, comingSoon: true },
]

const LOCATION_PRICE = 49

const BENEFIT_ICONS = [
  { color: brandRed,   shape: "square",   iconPath: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
  { color: brandGreen, shape: "circle",   iconPath: "M13 10V3L4 14h7v7l9-11h-7z" },
  { color: brandBlue,  shape: "triangle", iconPath: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" },
  { color: brandRed,   shape: "square",   iconPath: "M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" },
  { color: brandGreen, shape: "circle",   iconPath: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" },
  { color: brandBlue,  shape: "triangle", iconPath: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
]

const GYM_STATIC = [
  { name: "FlexGym Tirana",    instagram: "https://www.instagram.com/flexgym_tirane/",          image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/flexgym.jpg",    color: brandRed,   shape: "square"   as const },
  { name: "Flex Green Coast",  instagram: "https://www.instagram.com/flexgymgreencoast.al/",    image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/greencoast.jpg", color: brandGreen, shape: "circle"   as const },
  { name: "Flex Sports Club",  instagram: "https://www.instagram.com/flexsportsclub.al/",       image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/classes.jpg",   color: brandBlue,  shape: "triangle" as const },
]

// ─── Shapes ───────────────────────────────────────────────────────────────────

const ShapeDarkCtx = createContext(false)
type ShapeProps = { className?: string; size?: number; opacity?: number; animate?: string; color?: string }

const DARK_COLORS: Record<string, string> = {
  [brandRed]: brandRedLight,
  [brandGreen]: brandGreenLight,
  [brandBlue]: brandBlueLight,
}

function useShapeStyle(opacity: number, color: string) {
  const isDark = useContext(ShapeDarkCtx)
  return {
    opacity: isDark ? Math.min(opacity * 3, 0.30) : opacity,
    color: isDark ? (DARK_COLORS[color] ?? color) : color,
  }
}

function ShapeSquare({ className = "", size = 40, opacity = 0.15, animate = "", color = brandRed }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return <div className={`absolute ${animate} ${className}`} style={{ width: size, height: size, backgroundColor: s.color, opacity: s.opacity }} />
}
function ShapeCircle({ className = "", size = 40, opacity = 0.15, animate = "", color = brandGreen }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return <div className={`absolute rounded-full ${animate} ${className}`} style={{ width: size, height: size, backgroundColor: s.color, opacity: s.opacity }} />
}
function ShapeTriangle({ className = "", size = 40, opacity = 0.15, animate = "", color = brandBlue }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return (
    <svg className={`absolute ${animate} ${className}`} width={size} height={size} viewBox="0 0 40 40" style={{ opacity: s.opacity }}>
      <polygon points="2,2 38,2 20,38" fill={s.color} />
    </svg>
  )
}
function ShapeDiamond({ className = "", size = 40, opacity = 0.15, animate = "", color = brandRed }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return <div className={`absolute rotate-45 ${animate} ${className}`} style={{ width: size, height: size, backgroundColor: s.color, opacity: s.opacity }} />
}
function ShapeCross({ className = "", size = 40, opacity = 0.15, animate = "", color = brandGreen }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  const bar = size * 0.2
  return (
    <div className={`absolute ${animate} ${className}`} style={{ width: size, height: size, opacity: s.opacity }}>
      <div className="absolute top-1/2 left-0 -translate-y-1/2 rounded-full" style={{ width: size, height: bar, backgroundColor: s.color }} />
      <div className="absolute left-1/2 top-0 -translate-x-1/2 rounded-full" style={{ width: bar, height: size, backgroundColor: s.color }} />
    </div>
  )
}
function ShapeRing({ className = "", size = 40, opacity = 0.15, animate = "", color = brandBlue }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return <div className={`absolute rounded-full ${animate} ${className}`} style={{ width: size, height: size, border: `${Math.max(2, size * 0.1)}px solid ${s.color}`, opacity: s.opacity }} />
}

function CheckIcon({ color }: { color: string }) {
  return (
    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

// ─── Language Picker ──────────────────────────────────────────────────────────

function LanguagePicker({ lang, setLang, upward = false }: { lang: Lang; setLang: (l: Lang) => void; upward?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = LANGS.find(l => l.code === lang)!

  useEffect(() => {
    function onOutsideClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onOutsideClick)
    return () => document.removeEventListener("mousedown", onOutsideClick)
  }, [])

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors select-none"
        aria-label="Select language"
      >
        <span className="text-sm font-medium leading-none">{current.flag}</span>
        <span className="text-sm font-medium leading-none">–</span>
        <span className="text-sm font-medium leading-none">{current.label}</span>
        <svg
          className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className={`absolute right-0 w-44 rounded-2xl border bg-background shadow-xl overflow-hidden z-50 py-1 ${upward ? "bottom-full mb-2" : "top-full mt-2"}`}>
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false) }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-secondary ${lang === l.code ? "font-semibold" : "font-normal"}`}
            >
              <span className="text-sm leading-none flex-shrink-0">{l.flag}</span>
              <span className="flex-1 text-left leading-none">{l.label}</span>
              {lang === l.code && (
                <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function MainLandingPage() {
  const [selectedModules, setSelectedModules] = useState<Set<ModuleKey>>(new Set(["business"]))
  const [extraLocations, setExtraLocations] = useState(0)
  const [lang, setLang] = useState<Lang>("en")
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const t = translations[lang]

  const toggleModule = (id: ModuleKey) => {
    setSelectedModules(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => setSelectedModules(new Set(MODULES_CONFIG.map(m => m.id)))

  const modulesTotal = MODULES_CONFIG.filter(m => selectedModules.has(m.id)).reduce((sum, m) => sum + m.price, 0)
  const locationsTotal = extraLocations * LOCATION_PRICE
  const grandTotal = modulesTotal + locationsTotal

  return (
    <ShapeDarkCtx.Provider value={isDark}>
    <div className="min-h-screen bg-background overflow-hidden">
      <SEOHead
        title="THE NOT FIT - Enterprise Fitness Management Platform"
        description="Enterprise fitness management platform for modern gym chains and franchises. Membership management, classes, store, diet planning, and more."
      />

      {/* Navigation */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1.5">
              <span className="text-xl font-bold tracking-tight">THE</span>
              <div className="relative w-14 h-14">
                <img src="/logo.png" alt="THE NOT FIT Logo" className="w-full h-full object-contain" loading="eager" />
              </div>
              <span className="text-xl font-bold tracking-tight">FIT</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="ghost" asChild>
                <Link to="/login">{t.nav.signIn}</Link>
              </Button>
              <Button asChild style={{ backgroundColor: brandRed }} className="hover:opacity-90 text-white">
                <Link to="/signup">{t.nav.getStarted}</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-20 left-[8%] rotate-12" size={80} opacity={0.08} animate="animate-float" />
          <ShapeCircle className="top-32 right-[12%]" size={120} opacity={0.07} animate="animate-pulse-glow" />
          <ShapeTriangle className="bottom-16 left-[15%] -rotate-12" size={60} opacity={0.08} animate="animate-drift" />
          <ShapeSquare className="bottom-24 right-[8%] rotate-45" size={50} opacity={0.06} animate="animate-spin-slow" />
          <ShapeCircle className="top-48 left-[45%]" size={30} opacity={0.1} animate="animate-fade-float" />
          <ShapeDiamond className="top-12 left-[30%]" size={28} opacity={0.06} animate="animate-spin-slow-reverse" color={brandBlue} />
          <ShapeRing className="bottom-32 right-[25%]" size={70} opacity={0.06} animate="animate-float-delayed" />
          <ShapeCross className="top-[60%] left-[4%]" size={24} opacity={0.07} animate="animate-spin-slow" color={brandRed} />
        </div>

        <div className="container mx-auto px-4 py-20 md:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2">
                <div className="flex gap-0.5">
                  <div className="w-3 h-3" style={{ backgroundColor: brandRed }} />
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brandGreen }} />
                  <svg width="12" height="12" viewBox="0 0 12 12"><polygon points="0,0 12,0 6,12" fill={brandBlue} /></svg>
                </div>
                <span className="text-sm font-semibold px-3 py-1 rounded-full bg-secondary text-secondary-foreground">
                  {t.hero.badge}
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1]">
                {t.hero.heading1}{" "}
                <span className="bg-gradient-to-r from-[#C0392B] via-[#4A8C3F] to-[#2E5BA8] bg-clip-text text-transparent">
                  {t.hero.heading2}
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-xl">
                {t.hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button size="lg" asChild style={{ backgroundColor: brandRed }} className="hover:opacity-90 text-white text-base px-8">
                  <Link to="/signup">{t.hero.cta1}</Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="text-base px-8">
                  <Link to="/support">{t.hero.cta2}</Link>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">{t.hero.footnote}</p>
            </div>

            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://gyms.fra1.digitaloceanspaces.com/website/flex/flexgym.jpg"
                  alt="FlexGym Tirana interior"
                  className="w-full h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
              <div className="absolute -bottom-4 -left-4 w-20 h-20 flex items-center justify-center shadow-lg" style={{ backgroundColor: brandRed }}>
                <span className="text-white font-bold text-lg">10+</span>
              </div>
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: brandGreen }}>
                <span className="text-white font-bold text-sm text-center leading-tight">10K+<br />{t.hero.membersLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative border-y">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeTriangle className="top-4 left-[6%] rotate-6" size={36} opacity={0.05} animate="animate-float" />
          <ShapeDiamond className="bottom-4 right-[8%]" size={30} opacity={0.05} animate="animate-spin-slow" color={brandGreen} />
          <ShapeRing className="top-1/2 right-[3%] -translate-y-1/2" size={50} opacity={0.04} animate="animate-pulse-glow" color={brandRed} />
          <ShapeCross className="top-6 right-[40%]" size={20} opacity={0.05} animate="animate-drift" color={brandBlue} />
        </div>
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: `${brandRed}15` }}>
                <svg className="w-6 h-6" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: brandRed }}>10+</div>
                <div className="text-muted-foreground text-sm font-medium mt-1">{t.stats.gyms}</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${brandGreen}15` }}>
                <svg className="w-6 h-6" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: brandGreen }}>10,000+</div>
                <div className="text-muted-foreground text-sm font-medium mt-1">{t.stats.activeMembers}</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="relative w-12 h-12 flex items-center justify-center">
                <svg width="48" height="48" viewBox="0 0 48 48" style={{ position: "absolute", inset: 0 }}>
                  <polygon points="24,0 48,48 0,48" fill={`${brandBlue}15`} />
                </svg>
                <svg className="w-6 h-6 relative mt-2" style={{ color: brandBlue }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: brandBlue }}>99.9%</div>
                <div className="text-muted-foreground text-sm font-medium mt-1">{t.stats.uptimeSla}</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${brandRed}15` }}>
                <svg className="w-6 h-6" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: brandRed }}>24/7</div>
                <div className="text-muted-foreground text-sm font-medium mt-1">{t.stats.support}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-20 right-[5%] rotate-6" size={100} opacity={0.05} animate="animate-float" />
          <ShapeCircle className="bottom-32 left-[3%]" size={80} opacity={0.05} animate="animate-drift" />
          <ShapeTriangle className="top-1/2 right-[20%]" size={50} opacity={0.06} animate="animate-spin-slow" />
          <ShapeRing className="top-10 left-[20%]" size={60} opacity={0.04} animate="animate-float-delayed" color={brandRed} />
          <ShapeDiamond className="bottom-16 right-[12%]" size={35} opacity={0.05} animate="animate-spin-slow-reverse" color={brandGreen} />
          <ShapeCross className="bottom-48 left-[8%]" size={28} opacity={0.04} animate="animate-fade-float" color={brandBlue} />
          <ShapeCircle className="top-[35%] left-[50%]" size={22} opacity={0.04} animate="animate-pulse-glow" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center space-y-4 mb-20">
            <div className="flex justify-center gap-0.5 mb-4">
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandRed }} />
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandGreen }} />
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandBlue }} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{t.features.heading}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t.features.subheading}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Cards 0-1: Red / square */}
            {[0, 1].map(i => (
              <Card key={i} className="border-2 hover:border-[#C0392B]/50 transition-all hover:shadow-lg group">
                <CardHeader>
                  <div className="w-12 h-12 flex items-center justify-center mb-4" style={{ backgroundColor: `${brandRed}15` }}>
                    <svg className="w-6 h-6" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={i === 0
                        ? "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        : "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      } />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">{t.features.cards[i].title}</CardTitle>
                  <CardDescription className="text-base">{t.features.cards[i].desc_short}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{t.features.cards[i].desc}</p>
                </CardContent>
              </Card>
            ))}
            {/* Cards 2-3: Green / circle */}
            {[2, 3].map(i => (
              <Card key={i} className="border-2 hover:border-[#4A8C3F]/50 transition-all hover:shadow-lg group">
                <CardHeader>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${brandGreen}15` }}>
                    <svg className="w-6 h-6" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={i === 2
                        ? "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        : "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      } />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">{t.features.cards[i].title}</CardTitle>
                  <CardDescription className="text-base">{t.features.cards[i].desc_short}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{t.features.cards[i].desc}</p>
                </CardContent>
              </Card>
            ))}
            {/* Cards 4-5: Blue / triangle */}
            {[4, 5].map(i => (
              <Card key={i} className="border-2 hover:border-[#2E5BA8]/50 transition-all hover:shadow-lg group">
                <CardHeader>
                  <div className="w-12 h-12 flex items-center justify-center mb-4" style={{ backgroundColor: `${brandBlue}15`, clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" }}>
                    <svg className="w-6 h-6 mb-1" style={{ color: brandBlue }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={i === 4
                        ? "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        : "M12 6v6m0 0v6m0-6h6m-6 0H6"
                      } />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">{t.features.cards[i].title}</CardTitle>
                  <CardDescription className="text-base">{t.features.cards[i].desc_short}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{t.features.cards[i].desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative bg-secondary/20 py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeTriangle className="top-12 left-[5%] rotate-12" size={70} opacity={0.04} animate="animate-float" />
          <ShapeSquare className="bottom-20 right-[8%]" size={90} opacity={0.04} animate="animate-spin-slow" />
          <ShapeCircle className="top-[30%] right-[4%]" size={55} opacity={0.04} animate="animate-drift" />
          <ShapeRing className="bottom-[40%] left-[3%]" size={80} opacity={0.03} animate="animate-pulse-glow" color={brandRed} />
          <ShapeDiamond className="top-24 right-[45%]" size={26} opacity={0.05} animate="animate-fade-float" color={brandBlue} />
          <ShapeCross className="bottom-12 left-[20%]" size={22} opacity={0.04} animate="animate-spin-slow-reverse" color={brandRed} />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{t.pricing.heading}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t.pricing.subheading}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto mb-12">
            {/* Business Management */}
            <Card className="border-2 hover:border-[#C0392B]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandRed}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.business.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandRed }}>&euro;149</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.business.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandRed} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Classes */}
            <Card className="border-2 hover:border-[#C0392B]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandRed}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.classes.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandRed }}>&euro;149</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.classes.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandRed} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Workout & Training */}
            <Card className="border-2 hover:border-[#4A8C3F]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${brandGreen}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.workout.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandGreen }}>&euro;99</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.workout.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandGreen} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Diet Planner */}
            <Card className="border-2 hover:border-[#4A8C3F]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${brandGreen}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.diet.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandGreen }}>&euro;99</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.diet.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandGreen} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Store */}
            <Card className="border-2 hover:border-[#4A8C3F]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${brandGreen}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.store.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandGreen }}>&euro;99</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.store.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandGreen} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* White Label */}
            <Card className="border-2 hover:border-[#C0392B]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandRed}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.whitelabel.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandRed }}>&euro;99</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.whitelabel.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandRed} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Marketing & CRM — Coming Soon */}
            <Card className="border-2 hover:border-[#2E5BA8]/50 transition-all hover:shadow-lg relative">
              <div className="absolute -top-3 right-4">
                <span className="text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: brandBlue }}>{t.pricing.comingSoon}</span>
              </div>
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandBlue}15`, clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" }}>
                  <svg className="w-5 h-5 mb-1" style={{ color: brandBlue }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.marketing.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandBlue }}>&euro;49</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.marketing.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandBlue} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Payments — Coming Soon */}
            <Card className="border-2 hover:border-[#2E5BA8]/50 transition-all hover:shadow-lg relative">
              <div className="absolute -top-3 right-4">
                <span className="text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: brandBlue }}>{t.pricing.comingSoon}</span>
              </div>
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandBlue}15`, clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" }}>
                  <svg className="w-5 h-5 mb-1" style={{ color: brandBlue }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.payments.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandBlue }}>&euro;49</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.payments.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandBlue} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Extra Locations */}
            <Card className="border-2 transition-all hover:shadow-lg" style={{ borderColor: `${brandRed}40` }}>
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandRed}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.extraLocations.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandRed }}>&euro;49</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonthEach}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.extraLocations.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandRed} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Calculator */}
          <div className="max-w-2xl mx-auto">
            <Card className="border-2 bg-background/80 backdrop-blur">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-bold">{t.pricing.calculator.title}</h3>
                  <button
                    onClick={selectAll}
                    className="text-xs font-medium px-3 py-1.5 rounded-full border hover:bg-secondary transition-colors"
                  >
                    {t.pricing.calculator.selectAll}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                  {MODULES_CONFIG.map((m) => (
                    <label
                      key={m.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedModules.has(m.id)
                          ? "border-[#C0392B]/50 bg-[#C0392B]/5"
                          : "border-border hover:border-border/80"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedModules.has(m.id)}
                        onChange={() => toggleModule(m.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                          selectedModules.has(m.id) ? "border-[#C0392B] bg-[#C0392B]" : "border-muted-foreground/30"
                        }`}
                      >
                        {selectedModules.has(m.id) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{t.pricing.modules[m.id].name}</span>
                          {"comingSoon" in m && m.comingSoon && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: brandBlue }}>{t.pricing.soon}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold flex-shrink-0">&euro;{m.price}</span>
                    </label>
                  ))}
                </div>

                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{t.pricing.calculator.extraLocations}</span>
                    <span className="text-sm text-muted-foreground">&euro;{LOCATION_PRICE}{t.pricing.perMonthEach}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setExtraLocations(Math.max(0, extraLocations - 1))}
                      className="w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-secondary transition-colors text-lg font-medium"
                    >
                      &minus;
                    </button>
                    <div className="flex-1 bg-secondary/50 rounded-lg h-9 flex items-center justify-center">
                      <span className="text-sm font-bold">{extraLocations}</span>
                    </div>
                    <button
                      onClick={() => setExtraLocations(extraLocations + 1)}
                      className="w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-secondary transition-colors text-lg font-medium"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t.pricing.calculator.modules(selectedModules.size)}</span>
                    <span className="font-medium">&euro;{modulesTotal}{t.pricing.perMonth}</span>
                  </div>
                  {extraLocations > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.pricing.calculator.extraLocs(extraLocations)}</span>
                      <span className="font-medium">&euro;{locationsTotal}{t.pricing.perMonth}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t">
                    <span className="font-bold text-lg">{t.pricing.calculator.total}</span>
                    <span className="text-2xl font-bold" style={{ color: brandRed }}>&euro;{grandTotal}<span className="text-sm font-medium text-muted-foreground">{t.pricing.perMonth}</span></span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-muted-foreground mb-6">{t.pricing.cta.sub}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild style={{ backgroundColor: brandRed }} className="hover:opacity-90 text-white">
                <Link to="/signup">{t.pricing.cta.btn1}</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/support">{t.pricing.cta.btn2}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-16 right-[6%] rotate-12" size={65} opacity={0.05} animate="animate-float-delayed" />
          <ShapeCircle className="bottom-20 left-[5%]" size={90} opacity={0.04} animate="animate-pulse-glow" />
          <ShapeTriangle className="top-[40%] left-[2%]" size={40} opacity={0.05} animate="animate-drift" />
          <ShapeRing className="top-12 left-[40%]" size={45} opacity={0.04} animate="animate-spin-slow" color={brandGreen} />
          <ShapeDiamond className="bottom-32 right-[15%]" size={32} opacity={0.04} animate="animate-fade-float" color={brandBlue} />
        </div>
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <div className="relative flex justify-center lg:justify-center">
              <div className="absolute inset-0 blur-3xl opacity-20 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(ellipse at center, ${brandRed} 0%, ${brandBlue} 60%, transparent 100%)` }}
              />
              <div className="relative z-10 flex-shrink-0" style={{ width: 280, filter: "drop-shadow(0 32px 64px rgba(0,0,0,0.45))" }}>
                <div className="relative rounded-[44px] p-[3px]" style={{ background: "linear-gradient(145deg, #3a3a3c 0%, #1c1c1e 50%, #3a3a3c 100%)" }}>
                  <div className="absolute -left-[3px] top-[88px] w-[3px] h-8 rounded-l-full" style={{ backgroundColor: "#3a3a3c" }} />
                  <div className="absolute -left-[3px] top-[132px] w-[3px] h-12 rounded-l-full" style={{ backgroundColor: "#3a3a3c" }} />
                  <div className="absolute -left-[3px] top-[156px] w-[3px] h-12 rounded-l-full" style={{ backgroundColor: "#3a3a3c" }} />
                  <div className="absolute -right-[3px] top-[120px] w-[3px] h-16 rounded-r-full" style={{ backgroundColor: "#3a3a3c" }} />
                  <div className="rounded-[42px] overflow-hidden bg-black">
                    <div className="relative bg-black flex justify-center pt-3 pb-1 px-6">
                      <div className="w-24 h-6 rounded-full bg-black border border-zinc-800 flex items-center justify-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-zinc-700" />
                        <div className="w-3 h-3 rounded-full bg-zinc-700" />
                      </div>
                    </div>
                    <div className="relative" style={{ aspectRatio: "9/18" }}>
                      <video src="https://gyms.fra1.digitaloceanspaces.com/website/flex/flexaccess.mp4" autoPlay muted loop playsInline className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                    </div>
                    <div className="bg-black flex justify-center py-2">
                      <div className="w-24 h-1 rounded-full bg-zinc-600" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 items-end justify-center mt-5">
                  <div className="w-8 h-8" style={{ backgroundColor: brandRed, opacity: 0.9 }} />
                  <div className="w-6 h-6 rounded-full" style={{ backgroundColor: brandGreen, opacity: 0.9 }} />
                  <svg width="24" height="24" viewBox="0 0 40 40" style={{ opacity: 0.9 }}>
                    <polygon points="2,2 38,2 20,38" fill={brandBlue} />
                  </svg>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="flex gap-0.5 mb-4">
                  <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandRed }} />
                  <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandGreen }} />
                  <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandBlue }} />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">{t.benefits.heading}</h2>
                <p className="text-xl text-muted-foreground">{t.benefits.subheading}</p>
              </div>

              <div className="space-y-5">
                {BENEFIT_ICONS.map((icon, i) => {
                  const item = t.benefits.items[i]
                  return (
                    <div key={i} className="flex items-start gap-4">
                      {icon.shape === "triangle" ? (
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: icon.color, clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" }}>
                          <svg className="w-5 h-5 text-white mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon.iconPath} />
                          </svg>
                        </div>
                      ) : (
                        <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center ${icon.shape === "circle" ? "rounded-full" : ""}`} style={{ backgroundColor: icon.color }}>
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon.iconPath} />
                          </svg>
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold mb-0.5">{item.title}</h3>
                        <p className="text-muted-foreground text-sm">{item.desc}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study — Flex Gym Group */}
      <section className="relative bg-secondary/20 py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeCircle className="top-12 left-[10%]" size={60} opacity={0.06} animate="animate-float" />
          <ShapeSquare className="bottom-12 right-[10%] rotate-12" size={50} opacity={0.06} animate="animate-spin-slow" />
          <ShapeTriangle className="top-24 right-[20%]" size={40} opacity={0.05} animate="animate-drift" />
          <ShapeRing className="bottom-[35%] left-[4%]" size={55} opacity={0.04} animate="animate-pulse-glow" color={brandRed} />
          <ShapeCross className="top-[50%] right-[5%]" size={26} opacity={0.04} animate="animate-float-delayed" color={brandGreen} />
          <ShapeDiamond className="bottom-20 left-[35%]" size={24} opacity={0.04} animate="animate-fade-float" color={brandBlue} />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="flex gap-0.5">
                  <div className="w-2.5 h-2.5" style={{ backgroundColor: brandRed }} />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: brandGreen }} />
                  <svg width="10" height="10" viewBox="0 0 10 10"><polygon points="0,0 10,0 5,10" fill={brandBlue} /></svg>
                </div>
                <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t.caseStudy.label}</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                {t.caseStudy.heading1}{" "}
                <span className="bg-gradient-to-r from-[#C0392B] via-[#4A8C3F] to-[#2E5BA8] bg-clip-text text-transparent">
                  {t.caseStudy.heading2}
                </span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t.caseStudy.description}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {GYM_STATIC.map((gym, i) => {
                const info = t.caseStudy.gyms[i]
                return (
                  <div key={i} className="group relative bg-card border rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                    <div className="relative h-52 overflow-hidden">
                      <img src={gym.image} alt={gym.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                        <div>
                          <h3 className="text-white font-bold text-lg">{gym.name}</h3>
                          <p className="text-white/80 text-sm">{info.subtitle}</p>
                        </div>
                        <div
                          className={`w-12 h-12 flex items-center justify-center text-white font-bold text-xs shadow-lg ${gym.shape === "circle" ? "rounded-full" : ""}`}
                          style={{ backgroundColor: gym.color, ...(gym.shape === "triangle" ? { clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" } : {}) }}
                        >
                          {gym.shape !== "triangle" && info.members}
                        </div>
                      </div>
                    </div>
                    <div className="p-5 space-y-4">
                      <div className="flex items-center gap-2 -mt-1 mb-1">
                        <span className="text-xl font-bold" style={{ color: gym.color }}>{info.members}</span>
                        <span className="text-sm text-muted-foreground">{info.highlight}</span>
                      </div>
                      <ul className="space-y-2">
                        {info.features.map((f, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <div
                              className={`w-1.5 h-1.5 flex-shrink-0 ${gym.shape === "circle" ? "rounded-full" : ""}`}
                              style={{ backgroundColor: gym.color, ...(gym.shape === "triangle" ? { clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" } : {}) }}
                            />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <a
                        href={gym.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium hover:underline transition-colors"
                        style={{ color: gym.color }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                        </svg>
                        {t.caseStudy.followInstagram}
                      </a>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="bg-card border rounded-2xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="space-y-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5" style={{ color: brandRed }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <blockquote className="text-lg md:text-xl font-medium italic leading-relaxed">
                    {t.caseStudy.testimonial}
                  </blockquote>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 rounded-lg overflow-hidden" style={{ backgroundColor: "#000000", padding: "8px" }}>
                      <img src="https://gyms.fra1.digitaloceanspaces.com/logos/flexgym-ml9wd9l5/87ce35b0-0ce1-45aa-92c8-52bb71fb8a18.png" alt="Flex Gym Group logo" className="h-10 w-auto object-contain" />
                    </div>
                    <div className="space-y-0.5">
                      <div className="font-semibold">Flex Gym Group</div>
                      <div className="text-muted-foreground text-sm">{t.caseStudy.operator}</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: "3",      color: brandRed },
                    { value: "10,000+", color: brandGreen },
                    { value: "50+",    color: brandBlue },
                    { value: "1",      color: brandRed },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-4 rounded-xl" style={{ backgroundColor: `${stat.color}10` }}>
                      <div className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                      <div className="text-sm text-muted-foreground mt-1">{t.caseStudy.statsLabels[i]}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-1/4 left-[5%] rotate-45" size={150} opacity={0.04} animate="animate-spin-slow" />
          <ShapeCircle className="bottom-1/4 right-[5%]" size={200} opacity={0.04} animate="animate-pulse-glow" />
          <ShapeTriangle className="top-1/3 right-[15%] rotate-12" size={100} opacity={0.04} animate="animate-float" />
          <ShapeRing className="top-[15%] left-[25%]" size={70} opacity={0.04} animate="animate-drift" color={brandGreen} />
          <ShapeDiamond className="bottom-[20%] left-[12%]" size={40} opacity={0.04} animate="animate-float-delayed" color={brandBlue} />
          <ShapeCross className="top-[60%] right-[25%]" size={30} opacity={0.03} animate="animate-spin-slow-reverse" color={brandRed} />
          <ShapeCircle className="top-8 left-[50%]" size={35} opacity={0.04} animate="animate-fade-float" color={brandRed} />
        </div>

        <div className="container mx-auto px-4 text-center relative">
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="flex justify-center gap-1 mb-6">
              <div className="w-10 h-10" style={{ backgroundColor: brandRed }} />
              <div className="w-10 h-10 rounded-full" style={{ backgroundColor: brandGreen }} />
              <svg width="40" height="40" viewBox="0 0 40 40"><polygon points="2,2 38,2 20,38" fill={brandBlue} /></svg>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight">
              {t.cta.heading1}{" "}
              <span className="bg-gradient-to-r from-[#C0392B] via-[#4A8C3F] to-[#2E5BA8] bg-clip-text text-transparent">
                {t.cta.heading2}
              </span>
            </h2>
            <p className="text-xl text-muted-foreground">
              {t.cta.description.split("\n").map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Button size="lg" asChild style={{ backgroundColor: brandRed }} className="hover:opacity-90 text-white text-lg px-8 py-6 h-auto">
                <Link to="/signup">{t.cta.btn1}</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-lg px-8 py-6 h-auto">
                <Link to="/support">{t.cta.btn2}</Link>
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 pt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><CheckIcon color={brandRed} /><span>{t.cta.check1}</span></div>
              <div className="flex items-center gap-2"><CheckIcon color={brandGreen} /><span>{t.cta.check2}</span></div>
              <div className="flex items-center gap-2"><CheckIcon color={brandBlue} /><span>{t.cta.check3}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t bg-background overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-8 right-[6%] rotate-12" size={30} opacity={0.03} animate="animate-float" />
          <ShapeCircle className="bottom-6 left-[10%]" size={40} opacity={0.03} animate="animate-drift" />
          <ShapeTriangle className="top-1/2 right-[30%] -translate-y-1/2" size={25} opacity={0.03} animate="animate-spin-slow" />
        </div>
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <Link to="/" className="flex items-center gap-1.5">
                <span className="font-bold">THE</span>
                <div className="relative w-11 h-11">
                  <img src="/logo.png" alt="THE NOT FIT Logo" className="w-full h-full object-contain" />
                </div>
                <span className="font-bold">FIT</span>
              </Link>
              <p className="text-sm text-muted-foreground">{t.footer.tagline}</p>
              <div className="flex gap-0.5 pt-2">
                <div className="w-4 h-4" style={{ backgroundColor: brandRed }} />
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: brandGreen }} />
                <svg width="16" height="16" viewBox="0 0 16 16"><polygon points="0,0 16,0 8,16" fill={brandBlue} /></svg>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t.footer.product}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {t.footer.productLinks.map((l, i) => (
                  <li key={i}><Link to="/login" className="hover:text-foreground transition-colors">{l}</Link></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t.footer.company}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {t.footer.companyLinks.map((l, i) => (
                  <li key={i}><Link to="/support" className="hover:text-foreground transition-colors">{l}</Link></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t.footer.legal}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {["/privacy", "/privacy", "/data-deletion", "/support"].map((href, i) => (
                  <li key={i}><Link to={href} className="hover:text-foreground transition-colors">{t.footer.legalLinks[i]}</Link></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              <LanguagePicker lang={lang} setLang={setLang} upward />
              <p>{t.footer.copyright}</p>
            </div>
            <div className="flex gap-4">
              <a href="#" className="hover:text-foreground transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a href="#" className="hover:text-foreground transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
              </a>
              <a href="#" className="hover:text-foreground transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </ShapeDarkCtx.Provider>
  )
}
